import React, { useState, useEffect, useRef, useCallback } from 'react';

const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  blueDark:     '#3d5c9e',
  amber:        '#f0c74e',
  amberDark:    '#c9a12e',
};

const font = "'Nunito', -apple-system, sans-serif";
const mono = "'JetBrains Mono', 'Fira Mono', monospace";

// Canvas internal resolution — all coordinates fit within CANVAS_W × CANVAS_H
const CANVAS_W = 680;
const CANVAS_H = 470;

// Palette gradient by recursion level: teal-green (root/level 0) → indigo (deepest)
function levelColor(level, maxLevel) {
  const t = maxLevel <= 0 ? 0 : Math.min(1, level / maxLevel);
  const hue = 150 + t * 78;
  return { fill: `hsl(${hue.toFixed(0)}, 46%, 58%)`, edge: `hsl(${hue.toFixed(0)}, 46%, 40%)` };
}

// ── Recursive drawing routines ────────────────────────────────────────────────
// Each mutates a { n } counter so the UI can report how many shapes were drawn.

// Sierpiński triangle — a filled triangle that splits into three half-size copies.
function drawSierpinski(ctx, ax, ay, bx, by, cx, cy, depth, counter) {
  if (depth === 0) {
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    counter.n++;
    return;
  }
  const abx = (ax + bx) / 2, aby = (ay + by) / 2;
  const bcx = (bx + cx) / 2, bcy = (by + cy) / 2;
  const cax = (cx + ax) / 2, cay = (cy + ay) / 2;
  drawSierpinski(ctx, ax, ay, abx, aby, cax, cay, depth - 1, counter);
  drawSierpinski(ctx, abx, aby, bx, by, bcx, bcy, depth - 1, counter);
  drawSierpinski(ctx, cax, cay, bcx, bcy, cx, cy, depth - 1, counter);
}

// Koch curve — one straight segment that becomes four segments with a bump.
function drawKoch(ctx, x1, y1, x2, y2, depth, counter) {
  if (depth === 0) {
    ctx.lineTo(x2, y2);
    counter.n++;
    return;
  }
  const dx = (x2 - x1) / 3, dy = (y2 - y1) / 3;
  const xa = x1 + dx,     ya = y1 + dy;       // first third
  const xb = x1 + 2 * dx, yb = y1 + 2 * dy;   // second third
  // Peak of the outward equilateral bump (rotate the middle third by -60°)
  const ang = Math.atan2(y2 - y1, x2 - x1) - Math.PI / 3;
  const len = Math.hypot(dx, dy);
  const px = xa + Math.cos(ang) * len;
  const py = ya + Math.sin(ang) * len;
  drawKoch(ctx, x1, y1, xa, ya, depth - 1, counter);
  drawKoch(ctx, xa, ya, px, py, depth - 1, counter);
  drawKoch(ctx, px, py, xb, yb, depth - 1, counter);
  drawKoch(ctx, xb, yb, x2, y2, depth - 1, counter);
}

// Recursion (binary) tree — a branch that sprouts two shorter, rotated branches.
function drawTree(ctx, x, y, angle, len, depth, maxDepth, counter) {
  const x2 = x + Math.cos(angle) * len;
  const y2 = y + Math.sin(angle) * len;
  const { edge } = levelColor(maxDepth - depth, maxDepth);
  ctx.strokeStyle = edge;
  ctx.lineWidth = Math.max(1, depth * 0.7);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  counter.n++;
  if (depth === 0) return;               // base case — a leaf branch, stop here
  const spread = 0.42;                    // ~24° each side
  drawTree(ctx, x2, y2, angle - spread, len * 0.72, depth - 1, maxDepth, counter);
  drawTree(ctx, x2, y2, angle + spread, len * 0.72, depth - 1, maxDepth, counter);
}

// Pythagoras tree — a square with two smaller squares balanced on top.
function drawPythagoras(ctx, ax, ay, bx, by, depth, maxDepth, counter) {
  const vx = bx - ax, vy = by - ay;       // base edge a → b
  // Square grows "upward" (screen y is down): perpendicular = (vy, -vx)
  const cx = bx + vy, cy = by - vx;       // top-right
  const dx = ax + vy, dy = ay - vx;       // top-left
  const { fill, edge } = levelColor(maxDepth - depth, maxDepth);
  ctx.fillStyle = fill;
  ctx.strokeStyle = edge;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.lineTo(cx, cy);
  ctx.lineTo(dx, dy);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  counter.n++;
  if (depth === 0) return;                // base case — a leaf square, stop here
  // Apex of the right-isosceles triangle sitting on the top edge d → c
  const mx = (dx + cx) / 2, my = (dy + cy) / 2;
  const ex = mx + vy * 0.5, ey = my - vx * 0.5;
  drawPythagoras(ctx, dx, dy, ex, ey, depth - 1, maxDepth, counter); // left child
  drawPythagoras(ctx, ex, ey, cx, cy, depth - 1, maxDepth, counter); // right child
}

// ── Fractal registry ──────────────────────────────────────────────────────────

const FRACTALS = {
  sierpinski: {
    label: 'Sierpiński triangle',
    unit: 'triangles',
    maxDepth: 7,
    defaultDepth: 4,
    countFormula: 'shapes = 3ᵈ',
    count: (d) => Math.pow(3, d),
    pseudo:
`function sierpinski(triangle, depth):
    if depth == 0:                 # base case
        draw(triangle)             # a solid triangle
    else:
        a, b, c = midpoints(triangle)
        sierpinski(top,   depth-1) # three half-size
        sierpinski(left,  depth-1) # copies of the
        sierpinski(right, depth-1) # whole triangle`,
    draw: (ctx, depth) => {
      const counter = { n: 0 };
      ctx.fillStyle = B.green;
      ctx.strokeStyle = B.greenDark;
      ctx.lineWidth = depth >= 5 ? 0.4 : 0.8;
      drawSierpinski(ctx, 340, 30, 55, 445, 625, 445, depth, counter);
      return counter.n;
    },
  },
  koch: {
    label: 'Koch snowflake',
    unit: 'segments',
    maxDepth: 5,
    defaultDepth: 3,
    countFormula: 'segments = 3 · 4ᵈ',
    count: (d) => 3 * Math.pow(4, d),
    pseudo:
`function koch(p1, p2, depth):
    if depth == 0:                 # base case
        draw_line(p1, p2)          # a straight segment
    else:
        a, peak, b = split(p1, p2) # 1/3 points + bump
        koch(p1, a,    depth-1)    # four segments,
        koch(a,  peak, depth-1)    # each a smaller
        koch(peak, b,  depth-1)    # copy of the
        koch(b,  p2,   depth-1)    # whole curve`,
    draw: (ctx, depth) => {
      const counter = { n: 0 };
      // Equilateral triangle, vertices clockwise so the bumps point outward
      const cx = 340, cy = 260, R = 210;
      const pts = [-90, 30, 150].map(a => {
        const r = a * Math.PI / 180;
        return [cx + R * Math.cos(r), cy + R * Math.sin(r)];
      });
      ctx.strokeStyle = B.blue;
      ctx.fillStyle = 'rgba(90,122,191,0.08)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 0; i < 3; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % 3];
        drawKoch(ctx, x1, y1, x2, y2, depth, counter);
      }
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      return counter.n;
    },
  },
  tree: {
    label: 'Recursion tree',
    unit: 'branches',
    maxDepth: 10,
    defaultDepth: 6,
    countFormula: 'branches = 2ᵈ⁺¹ − 1',
    count: (d) => Math.pow(2, d + 1) - 1,
    pseudo:
`function tree(x, y, angle, len, depth):
    draw_branch(x, y, angle, len)
    if depth == 0:                 # base case
        return                     # a leaf: stop
    end = tip(x, y, angle, len)
    tree(end, angle - 24°, len*0.72, depth-1)
    tree(end, angle + 24°, len*0.72, depth-1)`,
    draw: (ctx, depth) => {
      const counter = { n: 0 };
      drawTree(ctx, 340, 452, -Math.PI / 2, 108, depth, depth, counter);
      return counter.n;
    },
  },
  pythagoras: {
    label: 'Pythagoras tree',
    unit: 'squares',
    maxDepth: 10,
    defaultDepth: 6,
    countFormula: 'squares = 2ᵈ⁺¹ − 1',
    count: (d) => Math.pow(2, d + 1) - 1,
    pseudo:
`function pythagoras(base_edge, depth):
    draw_square(base_edge)
    if depth == 0:                 # base case
        return                     # a leaf: stop
    left, right = top_edges(base_edge)
    pythagoras(left,  depth-1)     # two smaller
    pythagoras(right, depth-1)     # squares on top`,
    draw: (ctx, depth) => {
      const counter = { n: 0 };
      const half = 42;
      drawPythagoras(ctx, 340 - half, 452, 340 + half, 452, depth, depth, counter);
      return counter.n;
    },
  },
};

const FRACTAL_ORDER = ['sierpinski', 'koch', 'tree', 'pythagoras'];

// ── Self-similarity diagram (depth 0 → 1 → 2 of a Sierpiński triangle) ─────────

function MiniTriangle({ depth }) {
  const counter = { n: 0 };
  // Recurse to collect leaf-triangle vertices for a tiny SVG illustration
  const tris = [];
  const rec = (ax, ay, bx, by, cx, cy, d) => {
    if (d === 0) { tris.push([ax, ay, bx, by, cx, cy]); return; }
    const abx = (ax + bx) / 2, aby = (ay + by) / 2;
    const bcx = (bx + cx) / 2, bcy = (by + cy) / 2;
    const cax = (cx + ax) / 2, cay = (cy + ay) / 2;
    rec(ax, ay, abx, aby, cax, cay, d - 1);
    rec(abx, aby, bx, by, bcx, bcy, d - 1);
    rec(cax, cay, bcx, bcy, cx, cy, d - 1);
  };
  rec(50, 8, 8, 84, 92, 84, depth);
  counter.n = tris.length;
  return (
    <svg viewBox="0 0 100 96" style={{ width: '100%', height: 'auto', display: 'block' }}>
      {tris.map((t, i) => (
        <polygon key={i} points={`${t[0]},${t[1]} ${t[2]},${t[3]} ${t[4]},${t[5]}`}
          fill={B.green} stroke={B.greenDark} strokeWidth="0.6" />
      ))}
    </svg>
  );
}

function SelfSimilarityDiagram() {
  const steps = [
    { d: 0, caption: 'Depth 0: base case', sub: 'The simplest shape: one solid triangle. Recursion stops here.' },
    { d: 1, caption: 'Depth 1: recursive case', sub: 'The triangle is replaced by three half-size copies of itself.' },
    { d: 2, caption: 'Depth 2: self-similar', sub: 'Each copy is split the same way. The pattern repeats at every scale.' },
  ];
  return (
    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
      {steps.map((st, i) => (
        <div key={i} style={{
          flex: '1 1 150px', minWidth: 140,
          border: `1px solid ${B.border}`, borderRadius: 10, padding: '0.7rem 0.7rem 0.55rem',
          background: B.bg,
        }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{
              width: 20, height: 20, borderRadius: '50%', background: B.green, color: '#fff',
              fontSize: '0.72rem', fontWeight: 800, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>{i + 1}</span>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: B.text }}>{st.caption}</span>
          </div>
          <div style={{ padding: '0 12%' }}>
            <MiniTriangle depth={st.d} />
          </div>
          <div style={{ fontSize: '0.72rem', color: B.faint, lineHeight: 1.45, marginTop: 6 }}>
            {st.sub}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function FractalDrawingSimulator({ onBack }) {
  const canvasRef = useRef(null);

  const [type,     setType]     = useState('sierpinski');
  const [depth,    setDepth]    = useState(FRACTALS.sierpinski.defaultDepth);
  const [shapes,   setShapes]   = useState(0);
  const [hintOpen, setHintOpen] = useState(false);

  const fractal  = FRACTALS[type];
  const maxDepth = fractal.maxDepth;

  // Redraw whenever the fractal type or depth changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.fillStyle = B.surface;
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.lineJoin = 'round';
    ctx.lineCap  = 'round';
    const n = fractal.draw(ctx, depth);
    setShapes(n);
  }, [type, depth, fractal]);

  // Switching fractal: clamp the depth into the new fractal's sensible range
  const handleTypeChange = useCallback((next) => {
    setType(next);
    setDepth(d => Math.min(d, FRACTALS[next].maxDepth));
  }, []);

  // ── Styles (shared vocabulary with the other simulators) ─────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '0 1.5rem', height: 60, gap: '1rem',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
    },
    hlBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: '#efe7fb', color: '#7a5bbf', padding: '3px 7px', borderRadius: 6,
    },
    body: { maxWidth: 760, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: {
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: '1.25rem', marginBottom: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
    controlRow: { display: 'flex', gap: '1.4rem', flexWrap: 'wrap', alignItems: 'flex-end' },
    ctrlGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    ctrlLabel: {
      fontSize: '0.72rem', fontWeight: 700, color: B.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    },
    select: {
      fontFamily: font, fontSize: '0.88rem', border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '6px 10px', background: B.bg, color: B.text, cursor: 'pointer',
    },
    rangeHint: { fontSize: '0.72rem', color: B.faint },
    stat: { display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' },
    statNum: { fontSize: '1.35rem', fontWeight: 800, color: B.text, fontFamily: mono, lineHeight: 1 },
    statLabel: { fontSize: '0.66rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.04em' },
    canvas: { display: 'block', width: '100%', height: 'auto', borderRadius: 8, background: B.surface },
    expBtn: {
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'none', border: 'none', cursor: 'pointer', fontFamily: font,
      fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14,
    },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
    code: {
      fontFamily: mono, fontSize: '0.76rem', lineHeight: 1.7, color: B.text,
      background: B.bg, border: `1px solid ${B.border}`, borderRadius: 8,
      padding: '0.85rem 1rem', margin: '0.9rem 0 0', whiteSpace: 'pre', overflowX: 'auto',
    },
  };

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>Fractal Drawing</h1>
        <span style={s.badge}>B2.4.4</span>
        <span style={s.hlBadge}>HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Controls ───────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Configuration</div>
          <div style={s.controlRow}>
            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="fractal-type">Fractal</label>
              <select
                id="fractal-type"
                style={s.select}
                value={type}
                onChange={e => handleTypeChange(e.target.value)}
              >
                {FRACTAL_ORDER.map(k => (
                  <option key={k} value={k}>{FRACTALS[k].label}</option>
                ))}
              </select>
            </div>

            <div style={s.ctrlGroup}>
              <label style={s.ctrlLabel} htmlFor="depth-range">Recursion depth</label>
              <input
                id="depth-range"
                type="range" min="0" max={maxDepth} step="1"
                value={depth}
                onChange={e => setDepth(+e.target.value)}
                style={{ accentColor: B.green, width: 160 }}
              />
              <span style={s.rangeHint}>depth {depth} of {maxDepth}</span>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '1.4rem', alignItems: 'center' }}>
              <div style={s.stat}>
                <span style={{ ...s.statNum, color: B.greenDark }}>{depth}</span>
                <span style={s.statLabel}>Depth</span>
              </div>
              <div style={{ width: 1, height: 34, background: B.border }} />
              <div style={s.stat}>
                <span style={s.statNum}>{shapes.toLocaleString()}</span>
                <span style={s.statLabel}>{fractal.unit} drawn</span>
              </div>
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: B.muted, marginTop: '0.85rem', lineHeight: 1.55 }}>
            <strong>Try it:</strong> drag the depth slider from 0 upward and watch self-similar detail
            appear. Each step deeper multiplies the work: {fractal.label.toLowerCase()} draws{' '}
            <strong style={{ fontFamily: mono }}>{fractal.countFormula}</strong>, so the{' '}
            {fractal.unit} count grows <strong>exponentially</strong>.
          </p>
        </div>

        {/* ── Canvas ─────────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>{fractal.label} · depth {depth}</div>
          <canvas ref={canvasRef} style={s.canvas} />
        </div>

        {/* ── Recursion hint accordion ────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen(o => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              Hint: how recursion builds a fractal
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                A fractal is a shape made of <strong>smaller copies of itself</strong>. That makes it a
                natural fit for recursion: a <strong>base case</strong> draws the simplest shape, and a{' '}
                <strong>recursive case</strong> draws the shape as several scaled-down copies, each of which
                is drawn by the very same function.
              </p>

              <SelfSimilarityDiagram />

              <p style={s.expNote}>
                Every panel is the <strong>same function, one level deeper</strong>. It keeps calling itself
                on smaller pieces until it hits <strong>depth 0</strong>, the base case, which draws directly
                without recursing. Remove the base case and the recursion never stops (a stack overflow).
              </p>

              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: B.faint, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '1rem' }}>
                Pseudocode · {fractal.label}
              </div>
              <pre style={s.code}>{fractal.pseudo}</pre>

              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Why recursion fits</div>
                  <ul style={s.expList}>
                    <li>The shape is defined in terms of <strong>smaller versions of itself</strong>, self-similar subproblems.</li>
                    <li>A few lines describe <strong>infinite detail</strong>; an iterative version would be far messier.</li>
                    <li>The same idea powers <strong>Towers of Hanoi</strong>, <strong>quicksort</strong> and tree traversal.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Its limitations</div>
                  <ul style={s.expList}>
                    <li>The shape count grows <strong>exponentially</strong> ({fractal.countFormula}); each extra level multiplies the work.</li>
                    <li>Every call adds a frame to the <strong>call stack</strong>; deep recursion can <strong>overflow memory</strong>.</li>
                    <li>Rendering slows sharply at high depth, so a <strong>practical depth limit</strong> is always needed.</li>
                  </ul>
                </div>
              </div>

              <p style={s.expNote}>
                At the current depth this fractal draws <strong style={{ fontFamily: mono }}>{shapes.toLocaleString()}</strong> {fractal.unit}.
                Push the slider to its maximum and the count explodes, showing how a compact recursive
                definition can hide an <strong>exponential</strong> cost in time and memory.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
